import { Player } from '../game/Player';
import { Game } from '../game/Game';
import { Mask } from './Mask';
import { HermesMask } from './HermesMask';
import { PoseidonMask } from './PoseidonMask';
import { AresMask } from './AresMask';
import { HadesMask } from './HadesMask';
import { ApolloMask } from './ApolloMask';

export enum MaskType {
  HERMES = 0,
  POSEIDON = 1,
  ARES = 2,
  HADES = 3,
  APOLLO = 4
}

export class MaskManager {
  private masks: Map<MaskType, Mask> = new Map();
  private unlockedMasks: Set<MaskType> = new Set();
  private currentMask: MaskType | null = null;
  private player: Player;
  private game: Game;
  
  // UI elements
  private maskSlots: NodeListOf<Element>;
  
  constructor(player: Player, game: Game) {
    this.player = player;
    this.game = game;
    
    // Create all masks
    this.masks.set(MaskType.HERMES, new HermesMask(player));
    this.masks.set(MaskType.POSEIDON, new PoseidonMask(player));
    this.masks.set(MaskType.ARES, new AresMask(player, game));
    this.masks.set(MaskType.HADES, new HadesMask(player));
    this.masks.set(MaskType.APOLLO, new ApolloMask(player, game));
    
    // Get UI elements
    this.maskSlots = document.querySelectorAll('.mask-slot');
    
    // Update UI
    this.updateUI();
  }
  
  public unlockMask(maskType: MaskType): void {
    if (!this.unlockedMasks.has(maskType)) {
      this.unlockedMasks.add(maskType);
      this.updateUI();
      
      const mask = this.masks.get(maskType);
      if (mask) {
        this.game.showHint(`Acquired: ${mask.name}! Press ${maskType + 1} to equip.`, 4000);
      }
      
      // Auto-equip if no mask is equipped
      if (this.currentMask === null) {
        this.switchToMask(maskType);
      }
    }
  }
  
  public switchToMask(maskIndex: number): void {
    const maskType = maskIndex as MaskType;
    
    // Check if mask is unlocked
    if (!this.unlockedMasks.has(maskType)) {
      return;
    }
    
    // Deactivate current mask
    if (this.currentMask !== null) {
      const currentMaskObj = this.masks.get(this.currentMask);
      currentMaskObj?.deactivate();
    }
    
    // Activate new mask
    this.currentMask = maskType;
    const newMask = this.masks.get(maskType);
    newMask?.activate();
    
    this.updateUI();
  }
  
  public useAbility(): void {
    if (this.currentMask !== null) {
      const mask = this.masks.get(this.currentMask);
      mask?.useAbility();
    }
  }
  
  public update(deltaTime: number): void {
    // Update current mask
    if (this.currentMask !== null) {
      const mask = this.masks.get(this.currentMask);
      mask?.update(deltaTime);
    }
  }
  
  public isTimeSlowed(): boolean {
    const hermes = this.masks.get(MaskType.HERMES) as HermesMask;
    return hermes?.isAbilityActive() ?? false;
  }
  
  public canBreakWalls(): boolean {
    return this.currentMask === MaskType.ARES;
  }
  
  public getCurrentMask(): MaskType | null {
    return this.currentMask;
  }
  
  public getCurrentMaskObject(): Mask | null {
    if (this.currentMask === null) return null;
    return this.masks.get(this.currentMask) ?? null;
  }
  
  public hasMask(maskType: MaskType): boolean {
    return this.unlockedMasks.has(maskType);
  }
  
  private updateUI(): void {
    this.maskSlots.forEach((slot, index) => {
      const maskType = index as MaskType;
      
      // Update unlocked state
      slot.classList.toggle('unlocked', this.unlockedMasks.has(maskType));
      
      // Update active state
      slot.classList.toggle('active', this.currentMask === maskType);
    });
  }
  
  // Reset all masks (for level restart)
  public reset(): void {
    this.unlockedMasks.clear();
    this.currentMask = null;
    
    this.masks.forEach(mask => {
      mask.deactivate();
    });
    
    this.updateUI();
  }
}
